<template>
  <Dialog :open="isOpen" @update:open="handleClose">
    <DialogContent class="tw-max-w-md">
      <DialogHeader>
        <DialogTitle class="tw-flex tw-items-center tw-gap-2">
          <UserPlus class="tw-w-5 tw-h-5" />
          {{ t('user_invite.title') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('user_invite.description') }}
        </DialogDescription>
      </DialogHeader>

      <form @submit="handleSubmit" class="tw-space-y-4">
        <div class="tw-grid tw-grid-cols-2 tw-gap-4">
          <div>
            <Label for="firstName">{{ t('customer_management.forms.first_name') }}</Label>
            <Input id="firstName" v-model="formData.firstName" placeholder="John" required />
          </div>
          <div>
            <Label for="lastName">{{ t('customer_management.forms.last_name') }}</Label>
            <Input id="lastName" v-model="formData.lastName" placeholder="Doe" required />
          </div>
        </div>

        <div>
          <Label for="email">{{ t('common.titles.email') }}</Label>
          <Input
            id="email"
            type="email"
            v-model="formData.email"
            placeholder="john@example.com"
            required
          />
        </div>

        <div>
          <Label>{{ t('user_invite.roles') }}</Label>
          <div class="tw-grid tw-grid-cols-2 tw-gap-2 tw-mt-2">
            <div
              v-for="role in roleOptions"
              :key="role.id"
              class="tw-flex tw-items-center tw-space-x-2"
            >
              <FancyCheckbox
                :id="role.id"
                :modelValue="formData.roles.includes(role.id)"
                @update:modelValue="checked => handleRoleChange(role.id, checked)"
              />
              <Label :for="role.id" class="tw-text-sm">
                {{ role.label }}
              </Label>
            </div>
          </div>
          <p v-if="formData.roles.length === 0" class="tw-text-sm tw-text-muted-foreground tw-mt-1">
            {{ t('user_invite.select_role_hint') }}
          </p>
        </div>

        <div v-if="formData.roles.includes('approver')">
          <Label for="customerLimit">{{ t('user_invite.approver_limit') }}</Label>
          <ShadcnCurrencyInput
            id="customerLimit"
            v-model="approverLimit"
            :placeholder="t('customer_management.forms.limit_placeholder')"
            :class="
              cn(
                'tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
                {
                  'tw-border-red-500': shouldShowError('customerLimit'),
                }
              )
            "
          />
        </div>

        <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-pt-4">
          <Button type="button" variant="outline" @click="handleClose">
            {{ t('common.actions.cancel') }}
          </Button>
          <Button
            type="submit"
            :disabled="
              !formData.firstName ||
              !formData.lastName ||
              !formData.email ||
              formData.roles.length === 0
            "
          >
            {{ t('customer_management.forms.send_invite') }}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import FancyCheckbox from '@/components/shared/fancyTables/FancyCheckbox.vue';
import ShadcnCurrencyInput from '@/components/ui/currency-input/ShadcnCurrencyInput.vue';
import { cn } from '@/lib/utils';

const { t } = useI18n();

// Types
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles?: string[];
  approverLimit?: number;
}

interface UserInviteDialogProps {
  isOpen: boolean;
}

// Props
const props = defineProps<UserInviteDialogProps>();

// Emits
const emit = defineEmits<{
  close: [];
  invite: [user: User];
}>();

// State
const formData = ref({
  firstName: '',
  lastName: '',
  email: '',
  roles: [] as string[],
  approverLimit: 0,
});

const approverLimit = ref<number | null>(0);

// Constants
const roleOptions = [
  { id: 'purchaser', label: 'Purchaser' },
  { id: 'order-clearer', label: 'Order Clearer' },
  { id: 'payer', label: 'Payer' },
  { id: 'payment-clearer', label: 'Payment Clearer' },
  { id: 'approver', label: 'Approver' },
  { id: 'viewer', label: 'Viewer' },
];

// Methods
const handleSubmit = (e: Event) => {
  e.preventDefault();

  const user: User = {
    id: `temp-${Date.now()}`,
    firstName: formData.value.firstName,
    lastName: formData.value.lastName,
    email: formData.value.email,
    roles: formData.value.roles,
    approverLimit: formData.value.roles.includes('approver') ? approverLimit.value ?? 0 : undefined,
  };

  emit('invite', user);

  // Reset form
  formData.value = {
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
    approverLimit: 0,
  };
  approverLimit.value = 0;
};

const handleRoleChange = (roleId: string, checked: boolean) => {
  if (checked) {
    formData.value.roles = [...formData.value.roles, roleId];
  } else {
    formData.value.roles = formData.value.roles.filter(r => r !== roleId);
  }
};

const shouldShowError = (_field: string) => {
  // Show error state if approver role selected and no value provided
  return formData.value.roles.includes('approver') && approverLimit.value === null;
};

const handleClose = () => {
  formData.value = {
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
    approverLimit: 0,
  };
  emit('close');
};
</script>
